/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import useOcapiSite from '../commerce-api/hooks/useOcapiSite'
import useCustomer from '../commerce-api/hooks/useCustomer'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '../components/register'
import {noop} from '../utils/utils'
import {ACCOUNT_HREF, API_ERROR_MESSAGE, HYBRID_ACCOUNT} from '../constants'
import {useToast} from './use-toast'
import useHybridURL from './use-hybrid-url'
import useEffectEvent from './use-effect-event'
import {trackEvent} from '../tracking/analytics'
const LOGIN_VIEW = 'login'
const REGISTER_VIEW = 'register'
const PASSWORD_VIEW = 'password'

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    onPasswordResetSuccess = noop,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const customer = useCustomer()
    const {customPreferences} = useOcapiSite()
    const hybridURL = useHybridURL()
    const [currentView, setCurrentView] = useState(initialView)
    const form = useForm()
    const submittedEmail = useRef()
    const toast = useToast()

    const submitForm = async (data) => {
        form.clearErrors()

        return {
            login: handleLogin,
            register: handleRegister,
            password: handleResetPassword
        }[currentView](data)
    }

    const handleLogin = async (data) => {
        const loginResult = customer.login(data)
        const trackEventResult = trackEvent({event: 'login', email: data.email})
        // wait for tracking and login to complete, as otherwise the user might be redirected before the tracking is sent
        await Promise.allSettled([loginResult, trackEventResult])
        loginResult.catch((error) => {
            const message = error.isCustomerInactive
                ? formatMessage(error.message)
                : /invalid credentials/i.test(error.message)
                ? formatMessage({
                      defaultMessage:
                          "Something's not right with your email or password. Try again.",
                      id: 'auth_modal.error.incorrect_email_or_password'
                  })
                : formatMessage(API_ERROR_MESSAGE)
            form.setError('global', {type: 'manual', message})
        })
    }

    const handleRegister = async (data) => {
        const registerResult = customer.registerCustomer(data, {
            login: !customPreferences.doubleOptInEnabled
        })
        const newsletterResult = data.newsletter
            ? customer.subscribeToNewsletter({
                  email: data.email,
                  firstName: data.firstName,
                  lastName: data.lastName
              })
            : true
        const trackEventResult = trackEvent({
            event: 'account_create',
            email: data.email
        })
        // wait for tracking and registration to complete, as otherwise the user might be redirected before the tracking is sent
        await Promise.allSettled([registerResult, trackEventResult, newsletterResult])
        registerResult.catch((error) => {
            const errorMsg = formatMessage(
                /the login is already in use/i.test(error.message)
                    ? {
                          defaultMessage: 'This email is already registered.',
                          id: 'account-registration.msg.account_exists'
                      }
                    : {
                          defaultMessage: 'Hmmm! Something went wrong. Please try again.',
                          id: 'global.error.something_went_wrong'
                      }
            )
            form.setError('global', {type: 'manual', message: errorMsg})
        })
        if (!customPreferences.doubleOptInEnabled) {
            hybridURL(HYBRID_ACCOUNT, ACCOUNT_HREF)
        } else {
            toast({
                title: `${formatMessage({
                    defaultMessage:
                        'In order to login, you have to activate your account from the link we sent you in an email.',
                    id: 'account-registration.msg.double_opt_in'
                })}`
            })
            props.onClose()
        }
    }

    const handleResetPassword = async ({email}) => {
        try {
            await customer.getResetPasswordToken(email)
            // Execute action to be perfromed on successful passoword reset
            await onPasswordResetSuccess()
            submittedEmail.current = email
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
        }
    }

    // Reset form and local state when opening the modal
    const onOpen = useEffectEvent(() => {
        setCurrentView(initialView)
        submittedEmail.current = undefined
        form.reset()
    })
    useEffect(() => {
        if (props.isOpen) {
            onOpen()
        }
    }, [props.isOpen, onOpen])

    // Auto-focus the first field in each form view
    const fieldsRef = form.control?.fieldsRef?.current
    useEffect(() => {
        if (fieldsRef) {
            const initialField = {
                [LOGIN_VIEW]: 'email',
                [REGISTER_VIEW]: 'firstName',
                [PASSWORD_VIEW]: 'email'
            }[currentView]
            fieldsRef?.[initialField]?.ref.focus()
        }
    }, [currentView, fieldsRef])

    // Clear form state when changing views
    const onViewChange = useEffectEvent(() => {
        form.reset()
    })
    useEffect(() => {
        onViewChange(currentView)
    }, [currentView, onViewChange])

    const onCustomerChange = useEffectEvent((customer) => {
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const {isOpen} = props
        const isNowRegistered = isOpen && customer.isRegistered && (loggingIn || registering)

        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // We are done with the modal.
        props.onClose()

        if (customPreferences.doubleOptInEnabled && !customer.c_activated) {
            toast({
                title: `${formatMessage({
                    defaultMessage:
                        'In order to login, you have to activate your account from the link we sent you in an email.',
                    id: 'account-registration.msg.double_opt_in'
                })}`
            })
            return
        }

        // Show a toast only for those registered users returning to the site.
        if (loggingIn) {
            toast({
                title: `${formatMessage(
                    {
                        defaultMessage: 'Hello {name}, welcome!',
                        id: 'auth_modal.info.welcome_user'
                    },
                    {
                        name: customer?.firstName
                    }
                )} ${formatMessage({
                    defaultMessage: "You're now signed in.",
                    id: 'auth_modal.description.now_signed_in'
                })}`
            })

            // Execute action to be performed on successful login
            onLoginSuccess()
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }
    })
    useEffect(() => {
        if (customer) {
            onCustomerChange(customer)
        }
    }, [customer, onCustomerChange])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? props.onClose() : setCurrentView(LOGIN_VIEW)

    const PasswordResetSuccess = () => (
        <Stack spacing={8}>
            <Text color="stokkePink.dark" textStyle="Regular 45XLarge">
                <FormattedMessage
                    defaultMessage={'Password Reset'}
                    id="auth_modal.password_reset_success.title.password_reset"
                />
            </Text>
            <Text textStyle="Regular Medium">
                <FormattedMessage
                    defaultMessage="An email message with a link to reset your password will be sent to <b>{email}</b>."
                    id="auth_modal.password_reset_success.info.will_email_shortly"
                    values={{
                        email: submittedEmail.current,
                        // eslint-disable-next-line react/display-name
                        b: (chunks) => <b>{chunks}</b>
                    }}
                />
            </Text>

            <Button onClick={onBackToSignInClick}>
                <FormattedMessage
                    defaultMessage="Back to Sign In"
                    id="auth_modal.password_reset_success.button.back_to_sign_in"
                />
            </Button>
        </Stack>
    )

    return (
        <Modal variant="auth" closeOnOverlayClick={false} data-testid="sf-auth-modal" {...props}>
            <ModalOverlay />
            <ModalContent>
                <ModalCloseButton />
                <ModalBody>
                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            submitForm={submitForm}
                            clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                            clickForgotPassword={() => setCurrentView(PASSWORD_VIEW)}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                        <RegisterForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <ResetPasswordForm
                            form={form}
                            submitForm={submitForm}
                            clickSignIn={onBackToSignInClick}
                        />
                    )}
                    {form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                        <PasswordResetSuccess />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW]),
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func,
    onPasswordResetSuccess: PropTypes.func
}

/**
 *
 * @param {('register'|'login'|'password')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()

    return {
        initialView,
        isOpen,
        onOpen,
        onClose
    }
}
